import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, from } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import {
  AlertService,
  AlertType,
} from '../../../../services/alert/alert.service';
import { ProformsAdminService } from '../../../../services/proforms/proforms-admin.service';
import { ProformsSortingService } from '../../../../services/proforms/proforms-sorting.service';
import { ProFormsSectionAPIResponse } from '../../../../types/responses/proforms.responses';
import { ModalComponent } from '../../../modals/modal/modal.component';

@Component({
  selector: 'app-proforms-reorder-sections-modal',
  standalone: false,
  templateUrl: './proforms-reorder-sections-modal.component.html',
  styleUrl: './proforms-reorder-sections-modal.component.scss',
})
export class ProformsReorderSectionsModalComponent {
  @Input() formId: number;

  @ViewChild('reorderModal') reorderModal: ModalComponent;

  sections: ProFormsSectionAPIResponse[] = [];

  isSaving = false;

  private modalRef: NgbModalRef | null = null;

  constructor(
    private proformsAdminService: ProformsAdminService,
    private proformsSortingService: ProformsSortingService,
    private alertService: AlertService
  ) {}

  openModal(sections: ProFormsSectionAPIResponse[]) {
    this.sections = [...sections].sort((a, b) => a.sort_order - b.sort_order);
    this.reorderModal.open();
  }

  drop(event: CdkDragDrop<ProFormsSectionAPIResponse[]>) {
    if (
      event.currentIndex === 0 &&
      this.sections[event.previousIndex].is_conditional
    ) {
      return;
    }
    moveItemInArray(this.sections, event.previousIndex, event.currentIndex);
  }

  save() {
    if (this.sections.length < 2) {
      this.reorderModal.close();
      return;
    }

    this.isSaving = true;

    const sectionPairs = this.sections.slice(1).map((section, idx) => ({
      sectionId: section.id,
      refId: this.sections[idx].id,
    }));

    from(sectionPairs)
      .pipe(
        concatMap(({ sectionId, refId }) =>
          this.proformsSortingService.sortSection(sectionId, 'after', refId)
        )
      )
      .subscribe({
        error: () => {
          this.isSaving = false;
        },
        complete: () => {
          this.isSaving = false;
          this.stripInvalidConditionsAfterReorder(() => {
            this.proformsAdminService.getProForm(this.formId);
            this.reorderModal.close();
          });
        },
      });
  }

  private stripInvalidConditionsAfterReorder(onComplete: () => void) {
    // Build a map of questionId → section index in the new order
    const questionToSectionIndex = new Map<number, number>();
    this.sections.forEach((section, idx) => {
      (section.questions ?? []).forEach((q) => {
        questionToSectionIndex.set(q.id, idx);
      });
    });

    const toStrip = this.sections.filter((section, idx) => {
      if (!section.is_conditional) return false;
      if (idx === 0) return true;
      const condSectionIdx = section.conditional_question_id
        ? questionToSectionIndex.get(section.conditional_question_id)
        : undefined;
      return condSectionIdx !== undefined && condSectionIdx >= idx;
    });

    if (toStrip.length === 0) {
      onComplete();
      return;
    }

    forkJoin(
      toStrip.map((s) =>
        this.proformsAdminService.updateSection(s.id, {
          is_conditional: 0,
          conditional_question_id: null,
          conditional_question_answer_option_id: null,
        })
      )
    ).subscribe({
      next: () => {
        this.alertService.showAlert(
          'Conditional settings were removed from one or more sections because their position changed.',
          undefined,
          AlertType.PRIMARY
        );
        onComplete();
      },
      error: () => onComplete(),
    });
  }
}
